import { type CheckVehicleResponse, combine } from '@local/common-lib';
import { Clear as DeleteIcon } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Chip,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import type { FormikProps } from 'formik';
import { DateTime } from 'luxon';
import { type FC, Fragment } from 'react';
import { RelativeDate } from '../../../components/date/relative-date.js';
import { FormikTextField } from '../../../components/formik-fields/text-field.js';

export interface CheckVehicleForm {
  rego: string;
}

export interface VehicleCheckViewProps {
  form: FormikProps<CheckVehicleForm>;
  onVehicleRemove: (rego: string) => void;
  vehicles: CheckVehicleResponse[];
}

const Plate: FC<{ rego: string }> = ({ rego }) => (
  <Box sx={{ background: '#E0E0E0', borderRadius: '5px', p: '2px' }}>
    <Box
      sx={{
        border: 'solid black 2px',
        borderRadius: '3px',
        textAlign: 'center',
        width: 90,
      }}
    >
      <Typography
        color="black"
        sx={{ fontFamily: 'Share Tech Mono', fontSize: '1.3rem', px: '6px' }}
        variant="body2"
      >
        {rego}
      </Typography>
    </Box>
  </Box>
);

export const VehicleCheckView: FC<VehicleCheckViewProps> = ({
  form,
  onVehicleRemove,
  vehicles,
}) => {
  return (
    <Stack spacing={2}>
      {vehicles.map((vehicle, i) => (
        <Fragment key={vehicle.rego}>
          {i > 0 && <Divider />}
          <Stack direction="column" spacing={1}>
            <Stack
              alignItems="center"
              direction="row"
              justifyContent="space-between"
              spacing={1}
            >
              <Stack alignItems="center" direction="row" spacing={1}>
                <Plate rego={vehicle.rego} />
                {vehicle.reportedStolen === true ? (
                  <Chip color="error" label="STOLEN" />
                ) : vehicle.reportedStolen === false ? (
                  <Chip color="success" label="OK" variant="outlined" />
                ) : undefined}
              </Stack>
              <IconButton
                aria-label="delete"
                onClick={() => onVehicleRemove(vehicle.rego)}
              >
                <DeleteIcon />
              </IconButton>
            </Stack>
            <Typography variant="body2">
              {combine(
                ' ',
                vehicle.color,
                vehicle.make,
                vehicle.model,
                vehicle.type,
              )}
            </Typography>
            {!!vehicle.previousMentions?.length &&
              vehicle.previousMentions.map((mention) => (
                <Stack
                  direction="row"
                  key={mention.when.toString()}
                  spacing={1}
                >
                  <Typography color="text.secondary" variant="body2">
                    <RelativeDate date={DateTime.fromJSDate(mention.when)} />
                  </Typography>
                  <Typography variant="body2">{mention.what}</Typography>
                  {mention.whatDetails && (
                    <Typography variant="body2">
                      {' - '}
                      {mention.whatDetails}
                    </Typography>
                  )}
                </Stack>
              ))}
          </Stack>
        </Fragment>
      ))}

      <Stack alignItems="start" direction="row" spacing={1}>
        <FormikTextField
          formik={form}
          helperText="Enter vehicle registration"
          id="rego"
          inputProps={{
            maxLength: 6,
            onInput: (evt) => {
              const target = evt.target as HTMLInputElement;
              const sectionStart = target.selectionStart;
              const sectionEnd = target.selectionEnd;
              target.value = target.value
                .toUpperCase()
                .replace(/[^A-Z0-9]/, '');
              target.selectionStart = sectionStart;
              target.selectionEnd = sectionEnd;
            },
            onKeyDown: (evt) => {
              if (evt.key === 'Enter') {
                evt.preventDefault();
                form.handleSubmit();
              }
            },
            sx: {
              fontFamily: 'Share Tech Mono',
              fontSize: '1.3rem',
              py: '13px',
            },
          }}
          label="Vehicle check"
          name="rego"
          type="text"
        />
        <LoadingButton
          loading={form.isSubmitting}
          onClick={() => form.handleSubmit()}
          sx={{ height: 56 }}
          type="button"
          variant="contained"
        >
          Check
        </LoadingButton>
      </Stack>
    </Stack>
  );
};
